import type {
  MarginCenterCustomerRelationshipCoverage,
  MarginCenterCustomerRelationshipPeriodMetrics,
} from '../types/marginCenter';

import type {
  MarginCenterEconomicReading,
} from './customerProductEconomicReadings';

import type {
  RelationshipAlignedBucket,
  RelationshipEconomicInvestigation,
} from './relationshipEconomicInvestigation';
import { pricingNumberFormat } from '../../../modules/pricingJourney/formatNumber';

export interface RelationshipEconomicInterpretation {
  headline: string;
  observation: string;
  activity: string;
  secondaryTitle: string;
  secondary: string;
  caution: string;
}

type DifferenceField = 'primaryDifference' | 'secondaryDifference';

const MONTHS = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

function describeDate(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return value;
  const month = MONTHS[Number(match[2]) - 1];
  return month ? `${Number(match[3])} de ${month}` : value;
}

function interval(row: RelationshipAlignedBucket): string {
  const from = row.current.covered_start;
  const to = row.current.covered_end;
  const a = /^(\d{4})-(\d{2})-(\d{2})/.exec(from);
  const b = /^(\d{4})-(\d{2})-(\d{2})/.exec(to);
  if (!a || !b) return `${from}–${to}`;
  if (from === to) return `el ${describeDate(from)}`;
  if (a[1] === b[1] && a[2] === b[2]) {
    return `del ${Number(a[3])} al ${Number(b[3])} de ${MONTHS[Number(a[2]) - 1]}`;
  }
  return `del ${describeDate(from)} al ${describeDate(to)}`;
}

function join(items: string[]): string {
  return items.length === 0
    ? ''
    : items.length === 1
      ? items[0]
      : `${items.slice(0, -1).join(', ')} y ${items[items.length - 1]}`;
}

function notableDates(
  leaders: RelationshipAlignedBucket[],
  aligned: RelationshipAlignedBucket[],
  field: DifferenceField,
  subject: string,
): string {
  const ups = leaders.filter((row) => (row[field] ?? 0) > 0);
  const downs = leaders.filter((row) => (row[field] ?? 0) < 0);
  const parts = [
    ups.length ? `aumentos ${join(ups.map(interval))}` : null,
    downs.length ? `descensos ${join(downs.map(interval))}` : null,
  ].filter((part): part is string => part !== null);
  if (parts.length) {
    return `Entre las mayores diferencias de ${subject} se observan ${parts.join(' y también ')}.`;
  }
  if (aligned.some((row) => row[field] !== null)) {
    return `Las fechas que se pueden comparar no muestran diferencias apreciables de ${subject}.`;
  }
  return `No hay suficientes datos por fechas para localizar el cambio de ${subject}.`;
}

function formatNumber(value: number): string {
  return pricingNumberFormat({
    maximumFractionDigits: 2,
  }).format(value);
}

function activitySentence(
  current: MarginCenterCustomerRelationshipPeriodMetrics,
  comparison: MarginCenterCustomerRelationshipPeriodMetrics | null,
): string {
  if (!comparison) return 'No hay un periodo de referencia para comparar la actividad de compra.';
  const parts: string[] = [];
  if (current.transaction_count !== comparison.transaction_count) {
    parts.push(`${current.transaction_count > comparison.transaction_count ? 'más' : 'menos'} operaciones (${formatNumber(current.transaction_count)} frente a ${formatNumber(comparison.transaction_count)})`);
  } else {
    parts.push('el mismo número de operaciones');
  }
  if (current.active_days !== comparison.active_days) {
    parts.push(`${current.active_days > comparison.active_days ? 'más' : 'menos'} días con compra (${formatNumber(current.active_days)} frente a ${formatNumber(comparison.active_days)})`);
  } else {
    parts.push('el mismo número de días con compra');
  }
  let result = `En conjunto se registran ${join(parts)}.`;
  if (current.transaction_count > 0 && comparison.transaction_count > 0) {
    const now = current.units / current.transaction_count;
    const before = comparison.units / comparison.transaction_count;
    if (Number.isFinite(now) && Number.isFinite(before)) {
      const status = Math.abs(now - before) < 1e-9
        ? 'se mantiene'
        : now > before ? 'aumenta' : 'disminuye';
      result += ` La media de unidades por operación ${status} (${formatNumber(now)} frente a ${formatNumber(before)}).`;
    }
  }
  return result;
}

function aggregateMovement(direction: MarginCenterEconomicReading['primary']['direction']): string {
  switch (direction) {
    case 'up': return 'sube';
    case 'down': return 'baja';
    case 'unchanged': return 'se mantiene';
    default: return 'no se puede valorar';
  }
}

function secondarySentence(
  reading: MarginCenterEconomicReading,
  insight: RelationshipEconomicInvestigation,
): string {
  const name = reading.key === 'price_units' ? 'cantidad vendida' : 'margen';
  const leaders = insight.secondaryLeaders;
  if (leaders.length > 0) {
    return notableDates(leaders, insight.aligned, 'secondaryDifference', name);
  }
  if (insight.aligned.every((row) => row.secondaryDifference === null)) {
    return reading.secondary.direction === 'unchanged'
      ? `El ${name} se mantiene en el conjunto del periodo, pero faltan datos por fechas para estudiar su evolución.`
      : `El ${name} ${aggregateMovement(reading.secondary.direction)} en el total, pero no hay suficientes datos comparables por fechas para localizar cuándo ocurrió.`;
  }
  if (reading.secondary.direction === 'unchanged') {
    return `El ${name} se mantiene en el conjunto del periodo y las fechas comparables disponibles tampoco muestran cambios.`;
  }
  return `El ${name} ${aggregateMovement(reading.secondary.direction)} en el total, aunque las fechas comparables disponibles no permiten localizar con claridad ese cambio.`;
}

function financialCaveat(coverage: MarginCenterCustomerRelationshipCoverage | null): boolean {
  if (!coverage) return false;
  return coverage.current.cost_coverage_pct < 100
    || coverage.current.resolved_revenue_coverage_pct < 100
    || (coverage.comparison !== null && (
      coverage.comparison.cost_coverage_pct < 100
      || coverage.comparison.resolved_revenue_coverage_pct < 100
    ));
}

export function buildRelationshipEconomicInterpretation({
  reading,
  insight,
  current,
  comparison,
  coverage,
}: {
  reading: MarginCenterEconomicReading;
  insight: RelationshipEconomicInvestigation;
  current: MarginCenterCustomerRelationshipPeriodMetrics;
  comparison: MarginCenterCustomerRelationshipPeriodMetrics | null;
  coverage: MarginCenterCustomerRelationshipCoverage | null;
}): RelationshipEconomicInterpretation {
  const primaryName = reading.key === 'revenue_margin'
    ? 'ingresos'
    : reading.key === 'price_units' ? 'precio medio' : 'coste medio';
  const rises = insight.primaryLeaders.some((row) => (row.primaryDifference ?? 0) > 0);
  const falls = insight.primaryLeaders.some((row) => (row.primaryDifference ?? 0) < 0);
  let headline: string;
  if (rises && falls) {
    headline = `El cambio de ${primaryName} no es uniforme: hay subidas y bajadas en fechas distintas.`;
  } else if (rises) {
    headline = `Los aumentos de ${primaryName} se aprecian en fechas concretas.`;
  } else if (falls) {
    headline = `Los descensos de ${primaryName} se aprecian en fechas concretas.`;
  } else if (reading.primary.direction === 'unchanged' && insight.aligned.some((row) => row.primaryDifference !== null)) {
    headline = `El ${primaryName} se mantiene en el conjunto del periodo y no presenta cambios destacados en las fechas comparables.`;
  } else {
    headline = `El cambio global de ${primaryName} no puede situarse con claridad en fechas concretas.`;
  }

  const secondaryTitle = reading.key === 'price_units'
    ? '¿Qué ocurre con la cantidad?'
    : '¿Qué ocurre con el margen?';
  const observation = notableDates(
    insight.primaryLeaders,
    insight.aligned,
    'primaryDifference',
    primaryName,
  );
  const activity = activitySentence(current, comparison);
  const secondary = secondarySentence(reading, insight);
  const cautions: string[] = [];
  if (insight.partialOrUnequalBuckets > 0) {
    cautions.push('Algunas comparaciones abarcan periodos incompletos o de distinta duración.');
  }
  if (reading.key !== 'price_units' && financialCaveat(coverage)) {
    cautions.push('Hay costes sin resolver; la lectura financiera es parcial.');
  }
  if (insight.directions.evaluable < 2) {
    cautions.push('Faltan datos por fechas para seguir juntas ambas magnitudes.');
  }
  cautions.push('Esto no demuestra la causa del cambio ni recomienda un precio.');
  return {
    headline,
    observation,
    activity,
    secondaryTitle,
    secondary,
    caution: cautions.join(' '),
  };
}
